import React, { useState } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera } from '@fortawesome/free-solid-svg-icons'; //
import necklaceIcon from '../assets/icons/necklace.svg';
import braceletIcon from '../assets/icons/bracelet.svg';
import ringIcon from '../assets/icons/ring.svg';
import earringIcon from '../assets/icons/earring.svg';
import DOMPurify from 'dompurify';
import { faCoins } from '@fortawesome/free-solid-svg-icons';



// Required for React-Modal
Modal.setAppElement('#root');

// Helper function to strip HTML tags
const stripHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};
const getProductTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'necklace':
      return necklaceIcon;
    case 'bracelet':
      return braceletIcon;
    case 'ring':
      return ringIcon;
    case 'earring':
      return earringIcon;
    default:
      return null;
  }
};


const OrderList = ({ orders, fetchOrders, role, tab }) => {
  const [isEditWeightModalOpen, setIsEditWeightModalOpen] = useState(false);
  const [editWeightOrder, setEditWeightOrder] = useState(null);
  const [editWeightInput, setEditWeightInput] = useState("");
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [selectedOrderToComplete, setSelectedOrderToComplete] = useState(null);
  const [finalWeightInput, setFinalWeightInput] = useState("");
  const [finalWeights, setFinalWeights] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadOrder, setUploadOrder] = useState(null);
  const [isEditGoldRateModalOpen, setIsEditGoldRateModalOpen] = useState(false);
  const [selectedOrderForGoldRate, setSelectedOrderForGoldRate] = useState(null);
  const [goldRates, setGoldRates] = useState({});


  // Full View Modal state
  const [isFullViewOpen, setIsFullViewOpen] = useState(false);
  const [selectedFullOrder, setSelectedFullOrder] = useState(null);

  // Trigger printing (modal content only)
  const handlePrint = () => {
    window.print();
  };

  // Open the full details modal
  const handleFullView = async (order) => {
    setSelectedFullOrder(order);
    setIsFullViewOpen(true);

    // ✅ Mark as seen if pending and not seen
    if (tab === 'pending' && !order.isSeen) {
      try {
        await axios.put(`/api/orders/mark-seen/${order._id}`);
        fetchOrders(); // refresh notification count
      } catch (error) {
        console.error('Failed to mark order as seen:', error);
      }
    }
  };

  const openCompleteModal = (order, editMode = false) => {
    setSelectedOrderToComplete(order);
    setIsEditMode(editMode);
    if (order.products.length > 1) {
      const weights = {};
      order.products.forEach((p) => {
        const prodId = p._id ? p._id.toString() : p.id;
        weights[prodId] = p.finalWeight || p.dealtWeight || "";
      });
      setFinalWeights(weights);
    } else {
      setFinalWeightInput(order.products[0]?.finalWeight || order.products[0]?.dealtWeight || "");
    }

    setIsCompleteModalOpen(true);
  };


  const openEditWeightModal = (order) => {
    setEditWeightOrder(order);
    setEditWeightInput(order.products?.[0]?.finalWeight || "");
    setIsEditWeightModalOpen(true);
  };

  const openEditGoldRateModal = (order) => {
    setSelectedOrderForGoldRate(order);

    const initialRates = {};
    order.products.forEach((p) => {
      const id = p._id ? p._id.toString() : p.id;
      initialRates[id] = p.goldRate || '';
    });
    setGoldRates(initialRates);
    setIsEditGoldRateModalOpen(true);
  };



  // Cancel Order Modal functions
  const openCancelModal = (id) => {
    setSelectedOrderId(id);
    setIsCancelModalOpen(true);
  };
  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
  };
  const confirmCancelOrder = async () => {
    if (selectedOrderId) {
      await axios.put(
        `/api/orders/update/${selectedOrderId}`,
        { status: 'Cancelled' }
      );
      closeCancelModal();
      fetchOrders();
    }
  };

  // Show final weight unless status is pending/processing
  const showFinalWeight = tab !== 'pending' && tab !== 'processing';
  const processNotesHtml = (htmlString) => {
  // 1) Sanitize while allowing ALL formatting ReactQuill uses
  const clean = DOMPurify.sanitize(htmlString, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'i', 'em', 'u',
      'ul', 'ol', 'li',
      'span', 'div',
      'img', 'a'
    ],
    ALLOWED_ATTR: [
      'src', 'href', 'target', 'rel',
      'alt', 'style', 'class'
    ],
  });

  // 2) Post process image thumbnails
  const div = document.createElement('div');
  div.innerHTML = clean;

  const images = div.querySelectorAll('img');
  images.forEach((img) => {
    const src = img.src;

    img.style.maxWidth = '140px';
    img.style.maxHeight = '140px';
    img.style.objectFit = 'contain';
    img.style.cursor = 'pointer';
    img.removeAttribute('width');
    img.removeAttribute('height');

    const link = document.createElement('a');
    link.href = src;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';

    img.parentNode.insertBefore(link, img);
    link.appendChild(img);
  });

  return div.innerHTML;
};


  return (
    <div className="p-2 sm:p-4 w-full bg-white rounded-lg shadow-lg">
      {/* Orders Table */}
      <div className="overflow-x-auto">

        <button
          className="mb-4 px-4 py-2 bg-purple-700 text-white rounded hover:bg-purple-800"
          onClick={() => {
            // Flatten multi-product orders into individual rows while keeping orderId
            const expandedOrders = orders.flatMap(order => {
              return order.products.map((product, idx) => ({
                ...order,
                product: product,
                _multi: order.products.length > 1,
                _firstProduct: idx === 0
              }));
            });

            localStorage.setItem("ordersToPrint", JSON.stringify(expandedOrders));
            window.open('/print-orders', '_blank');
          }}
        >
          🖨️ Print Table
        </button>

        <table className="min-w-[900px] w-full border-collapse border border-gray-300 text-xs sm:text-sm">        <thead className="bg-gray-100 text-sm">
          <tr>
            <th className="border text-center">Order Number</th>
            <th className="border text-center">Product ID</th>
            <th className="border">Date</th>
            <th className="border">Model Name</th>
            <th className="border">Product Type</th>
            <th className="border">Custom Name</th>
            <th className="border">Dealt Weight</th>
            {showFinalWeight && <th className="border">Final Weight</th>}
            <th className="border">Notes</th>
            <th className="border">Status</th>
            <th className="border">Actions</th>
            {tab !== 'completed' && <th className="border text-center">Days Left</th>}
          </tr>
        </thead>
          <tbody>
            {orders.map((order) => {

              return (
                <tr
                  key={order._id}
                  className={`transition-all duration-200 hover:bg-gray-50 hover:shadow-sd ${order.products?.length > 1 ? "bg-yellow-100" : ""
                    }`}
                >

                  <td className="border p-2 text-center ">{order.orderId} {role === "factory" && tab === "pending" && !order.isSeen && (
                    <span className="ml-2 inline-block text-xs text-white bg-red-500 px-2 py-0.5 rounded-full animate-bounce">
                      NEW
                    </span>
                  )}</td>

                  <td className="border text-center">
                    {order.products?.length === 1
                      ? order.products[0]?.productId || "—"
                      : "Multiple"}

                  </td>

                  <td className="border text-center">
                    {new Date(order.orderDate).toLocaleDateString()}
                  </td>
                  <td className="border text-center">
                    <div
                      className="line-clamp-1 max-w-[140px] truncate"
                      title={order.products?.[0]?.modelName || ''}
                    >
                      {order.products?.[0]?.modelName || '—'}
                    </div>
                  </td>
                  <td className="border text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className="capitalize">{order.products?.[0]?.productType || '—'}</span>
                      {getProductTypeIcon(order.products?.[0]?.productType) && (
                        <img
                          src={getProductTypeIcon(order.products[0].productType)}
                          alt={order.products[0].productType}
                          className="w-4 h-4"
                          style={{ display: 'inline-block' }}
                        />
                      )}
                    </div>
                  </td>

                  <td className="border text-center">
                    <div
                      className="line-clamp-1 max-w-[140px] truncate"
                      title={order.products?.[0]?.customName || ''}
                    >
                      {order.products?.[0]?.customName || '—'}
                    </div>
                  </td>
                  <td className="border text-center">{order.products?.[0]?.dealtWeight || '—'}</td>
                  {showFinalWeight && (
                    <td className="border text-center">  {order.products?.[0]?.finalWeight || '—'}
                    </td>
                  )}
                  <td className="border text-sm text-center">
                    {order.products?.[0]?.notes ? (
                      <div
                        title={stripHtml(order.products[0].notes).slice(0, 200)}
                        className="line-clamp-1"
                      >
                        {stripHtml(order.products[0].notes).slice(0, 30)}...
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>

                  <td className="border text-center">{order.status}</td>
                  <td className="border text-center">
                    {role === 'factory' && tab === 'pending' && (
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2 justify-start items-center">
                        <button
                          className="px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => handleFullView(order)}
                        >
                          View
                        </button>
                        <button
                          className="px-3 py-1 text-sm rounded bg-green-500 text-white hover:bg-green-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() =>
                            axios
                              .put(`/api/orders/update/${order._id}`, { status: 'Processing' })
                              .then(fetchOrders)
                          }
                        >
                          <span className="hidden sm:inline">Start Working</span>
                          <span className="inline sm:hidden">Start</span>
                        </button>

                        <button
                          className="px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => openCancelModal(order._id)}
                        >
                          Cancel
                        </button>
                      </div>

                    )}

                    {role === 'factory' && tab === 'processing' && (
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2 justify-start items-center">
                        <button
                          className="px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => handleFullView(order)}
                        >
                          View
                        </button>
                        <button className="px-3 py-1 text-sm rounded bg-green-500 text-white hover:bg-green-600 transform transition-transform duration-150 active:scale-95" onClick={() => openCompleteModal(order)}>
                          Complete
                        </button>

                        <button
                          className="px-3 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => openCancelModal(order._id)}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    {role === 'factory' && tab === 'completed' && (
                      <div className="flex flex-wrap gap-1 justify-start items-center ml-2">
                        <button
                          className="px-1 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => handleFullView(order)}
                        >
                          View
                        </button>
                        <button
                          className="px-1  py-1 text-sm rounded bg-green-700 text-white hover:bg-green-900 transform transition-transform duration-150 active:scale-95"
                          onClick={() =>
                            window.open(`/invoice/${order._id}`, '_blank')
                          }
                        >
                          Print Invoice
                        </button>
                        <button
                          className="px-1 py-1 text-sm rounded bg-yellow-600 text-white hover:bg-yellow-700 ..."
                          onClick={() => openCompleteModal(order, true)}
                        >
                          Edit
                        </button>


                        {order.products.length === 1 ? (
                          <label className="cursor-pointer inline-flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white px-1 py-1 rounded text-sm">
                            📤
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  const formData = new FormData();
                                  formData.append("productFinalImage", file);
                                  formData.append("productId", order.products[0]._id); // single product ID
                                  axios
                                    .post(`/api/orders/upload-photo/${order._id}`, formData)
                                    .then(() => {
                                      alert("Photo uploaded successfully!");
                                      fetchOrders();
                                    })
                                    .catch((error) => {
                                      console.error(error);
                                      alert("Error uploading photo.");
                                    });
                                }
                              }}
                            />
                          </label>
                        ) : (
                          <button
                            onClick={() => {
                              setUploadOrder(order);
                              setIsUploadModalOpen(true);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                          >
                            📤
                          </button>
                        )}


                        <button
                          className="px-1 py-1 text-sm rounded bg-red-500 text-white hover:bg-red-600 transform transition-transform duration-150 active:scale-95"
                          onClick={() => openCancelModal(order._id)}
                        >
                          Cancel
                        </button>
                        <td className="border text-center">
                          {order.products?.length > 0 &&
                            order.products.every(p => p.productFinalImage) && (
                              <a
                                href={`/uploads/${order.products[0].productFinalImage}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="All product images uploaded"
                              >
                                <FontAwesomeIcon icon={faCamera} className="text-blue-500" size="2x" />
                              </a>
                            )}
                        </td>

                        <button
                          className="px-2 py-1 text-sm rounded bg-yellow-500 text-white hover:bg-yellow-600 transform transition-transform duration-150 active:scale-95"
                          title="Edit Gold Rate"
                          onClick={() => openEditGoldRateModal(order)}
                        >
                          <FontAwesomeIcon icon={faCoins} />
                        </button>

                      </div>
                    )}

                    {role === 'customer-service' && (
                      <div className="flex gap-2 items-center justify-center">
                        <button className="px-3 py-1 rounded bg-blue-500 text-white transform transition-transform duration-150 active:scale-95" onClick={() => handleFullView(order)}>
                          View
                        </button>
                        {order.source === 'direct-customer' && (
                          <>
                            <select
                              value={order.status}
                              onChange={async e => {
                                await axios.put(`/api/orders/update/${order._id}`, { status: e.target.value });
                                fetchOrders();
                              }}
                              className="border p-1 rounded"
                            >
                              <option>Pending</option>
                              <option>Processing</option>
                              <option>Delivering</option>
                              <option>Delivered</option>
                            </select>
                            <button className="px-3 py-1 rounded bg-green-600 text-white transform transition-transform duration-150 active:scale-95" onClick={() => window.open(`/invoice/${order._id}`, '_blank')}>
                              Print Invoice
                            </button>
                          </>
                        )}
                        <button className="px-3 py-1 rounded bg-red-500 text-white transform transition-transform duration-150 active:scale-95" onClick={() => openCancelModal(order._id)}>
                          Cancel
                        </button>
                      </div>
                    )}



                  </td>
                  {tab !== 'completed' && (
                    <td className="border text-center">
                      {(() => {
                        if (!order.Dtime) return "—";

                        const today = new Date();
                        const deadline = new Date(order.Dtime);
                        const diffTime = deadline.getTime() - today.getTime();
                        const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        let bgColor = "bg-green-100 text-green-800";
                        if (daysLeft <= 7 && daysLeft >= 3) bgColor = "bg-yellow-100 text-yellow-800";
                        if (daysLeft < 3) bgColor = "bg-red-100 text-red-800";

                        return (
                          <span className={`px-2 py-1 rounded text-sm font-semibold ${bgColor}`}>
                            {isNaN(daysLeft) ? "—" : `${daysLeft} day${Math.abs(daysLeft) !== 1 ? "s" : ""}`}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={isEditGoldRateModalOpen}
        onRequestClose={() => setIsEditGoldRateModalOpen(false)}
        className="p-6 bg-white rounded-lg shadow max-w-2xl mx-auto mt-20 overflow-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <h2 className="text-xl font-bold mb-4">
          Edit Gold Rate - {selectedOrderForGoldRate?.orderId}
        </h2>

        {selectedOrderForGoldRate?.products?.map((p) => {
          const id = p._id ? p._id.toString() : p.id;
          return (
            <div key={id} className="border p-3 rounded mb-3">
              <p className="font-semibold">{p.modelName}</p>
              <p>Current Gold Rate: <span className="text-blue-700">{p.goldRate}</span></p>
              <input
                type="number"
                value={goldRates[id] || ''}
                onChange={(e) =>
                  setGoldRates((prev) => ({ ...prev, [id]: e.target.value }))
                }
                placeholder="Enter new gold rate"
                className="w-full border p-2 rounded mt-2"
              />
            </div>
          );
        })}

        <div className="flex justify-end gap-2 mt-4">
          <button
            className="bg-gray-400 text-white px-4 py-2 rounded"
            onClick={() => setIsEditGoldRateModalOpen(false)}
          >
            Cancel
          </button>
          <button
            className="bg-green-600 text-white px-4 py-2 rounded"
            onClick={async () => {
              try {
                await axios.put(
                  `/api/orders/update-gold-rate/${selectedOrderForGoldRate._id}`,
                  { goldRates: JSON.stringify(goldRates) }
                );
                alert('Gold rate(s) updated successfully.');
                setIsEditGoldRateModalOpen(false);
                fetchOrders();
              } catch (err) {
                console.error(err);
                alert('Failed to update gold rate.');
              }
            }}
          >
            Save
          </button>
        </div>
      </Modal>

      {/* CANCEL ORDER MODAL */}
      <Modal
        isOpen={isCancelModalOpen}
        onRequestClose={closeCancelModal}
        className="bg-white p-6 rounded-lg shadow-lg max-w-md mx-auto mt-20 text-center"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <h2 className="text-xl font-bold text-red-600 mb-4">Cancel Order</h2>
        <p className="text-gray-700">
          Are you sure you want to cancel this order?
        </p>
        <div className="mt-6 flex justify-center space-x-4">
          <button
            className="bg-gray-400 hover:bg-gray-500 text-white font-bold px-4 py-2 rounded transform transition-transform duration-150 active:scale-95"
            onClick={closeCancelModal}
          >
            No
          </button>
          <button
            className="bg-red-500 hover:bg-red-700 text-white font-bold px-4 py-2 rounded transform transition-transform duration-150 active:scale-95"
            onClick={confirmCancelOrder}
          >
            Yes, Cancel
          </button>
        </div>
      </Modal>

      {/* FULL VIEW MODAL (PRINT) */}
      <Modal
        isOpen={isFullViewOpen}
        onRequestClose={() => setIsFullViewOpen(false)}
        className="p-4 sm:p-6 bg-white rounded-lg shadow-lg w-[95vw] sm:w-[90%] max-w-6xl mx-auto mt-10 printable-modal min-h-[600px] overflow-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >


        {selectedFullOrder && (
          <div className="min-h-[600px]">
            <h2 className="text-2xl font-bold mb-4">Order Full Details</h2>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div><strong>Order Number:</strong> {selectedFullOrder.orderId}</div>
              <div><strong>Status:</strong> {selectedFullOrder.status}</div>
              <div><strong>Customer Name:</strong> {selectedFullOrder.customerName}</div>
              <div><strong>Phone Number:</strong> {selectedFullOrder.phoneNumber}</div>
              <div>
                <strong>Address:</strong> {selectedFullOrder.address || "—"}
              </div>

            </div>

            <h3 className="text-lg font-semibold mt-2 mb-2">Products:</h3>
            <div className="overflow-x-auto">

              <table className="table-auto w-full border border-gray-300 text-sm mb-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-1">Product ID</th>
                    <th className="border p-1">Model Name</th>
                    <th className="border p-1">Product Type</th>
                    <th className="border p-1">Custom Name</th>
                    <th className="border p-1">Dealt Price</th>
                    <th className="border p-1">Gold Rate</th>
                    <th className="border p-1">Dealt Weight</th>
                    <th className="border p-1">Final Weight</th>
                    <th className="border p-1">Guarantee</th>
                    <th className="border p-1">Model Photo</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFullOrder.products?.map((p, index) => (
                    <React.Fragment key={index}>
                      <tr>
                        <td className="border p-1 text-center">{p.productId}</td>
                        <td className="border p-1 text-center">{p.modelName}</td>
                        <td className="border p-1 text-center">{p.productType}</td>
                        <td className="border p-1 text-center">{p.customName || "—"}</td>
                        <td className="border p-1 text-center">{p.dealtPrice}</td>
                        <td className="border p-1 text-center">{p.goldRate}</td>
                        <td className="border p-1 text-center">{p.dealtWeight}</td>
                        <td className="border p-1 text-center">{p.finalWeight || "—"}</td>
                        <td className="border p-1 text-center">{p.guarantee || "—"}</td>
                        <td className="border p-1 text-center">
                          {p.modelPhoto ? (
                            <a href={`/uploads/${p.modelPhoto}`} target="_blank" rel="noreferrer">
                              <img
                                src={`/uploads/${p.modelPhoto}`}
                                alt="preview"
                                className="w-10 h-10 object-cover inline-block"
                              />
                            </a>
                          ) : "—"}
                        </td>
                      </tr>

                      {/* Notes Row */}
                      {p.notes && (
                        <tr>
                          <td colSpan="10" className="border-t px-3 py-2 bg-gray-50">
                            <strong className="block mb-1">Notes:</strong>
                            <div
                              className="text-sm notes-content"
                              dangerouslySetInnerHTML={{ __html: processNotesHtml(p.notes) }}
                            />
                          </td>
                        </tr>
                      )}

                    </React.Fragment>
                  ))}
                </tbody>

              </table>
            </div>
            <div className="flex justify-end space-x-2 mt-4 print-hide">
              <button
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                onClick={handlePrint}
              >
                Print Order
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                onClick={() => setIsFullViewOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}

      </Modal>

      <Modal
        isOpen={isCompleteModalOpen}
        onRequestClose={() => setIsCompleteModalOpen(false)}
        className="p-6 bg-white rounded-lg shadow max-w-2xl mx-auto mt-20 overflow-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        {selectedOrderToComplete && (
          <div>
            <h2 className="text-xl font-bold mb-4">
              Complete Order - {selectedOrderToComplete.orderId}
            </h2>
            {selectedOrderToComplete.products.length > 1 ? (
              // Multi-product order modal
              <div>
                {selectedOrderToComplete.products.map((product) => {
                  const prodId = product._id ? product._id.toString() : product.id;
                  return (
                    <div key={prodId} className="mb-4 border p-2 rounded">
                      <p className="font-semibold">{product.modelName}</p>
                      <p>Dealt Weight: {product.dealtWeight}</p>
                      <input
                        type="number"
                        placeholder="Enter final weight"
                        value={finalWeights[prodId] || ""}
                        onChange={(e) =>
                          setFinalWeights((prev) => ({
                            ...prev,
                            [prodId]: e.target.value,
                          }))
                        }
                        className="w-full border p-2 rounded"
                      />
                    </div>
                  );
                })}
                <div className="flex justify-end space-x-2 mt-4">
                  <button
                    className="bg-gray-400 text-white px-4 py-2 rounded"
                    onClick={() => setIsCompleteModalOpen(false)}
                  >
                    Close
                  </button>
                  {tab !== "completed" && (
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded"
                      onClick={async () => {
                        try {
                          await axios.put(
                            `/api/orders/complete-order/${selectedOrderToComplete._id}`,
                            {
                              finalWeights: JSON.stringify(finalWeights),
                            }
                          );
                          alert("Final weights saved.");
                          fetchOrders(); // Refresh order list if needed
                        } catch (err) {
                          alert("Failed to save final weights.");
                          console.error(err);
                        }
                      }}
                    >
                      Save
                    </button>
                  )}

                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                    onClick={async () => {
                      // Check that every product has a final weight
                      const allFilled = selectedOrderToComplete.products.every(
                        (p) => finalWeights[p._id ? p._id.toString() : p.id]
                      );
                      if (!allFilled) {
                        alert("Please enter final weights for all products.");
                        return;
                      }
                      try {
                        await axios.put(
                          `/api/orders/complete-order/${selectedOrderToComplete._id}`,
                          {
                            finalWeights: JSON.stringify(finalWeights),
                            complete: "true",
                          }
                        );
                        alert("Order completed.");
                        setIsCompleteModalOpen(false);
                        fetchOrders();
                      } catch (err) {
                        alert("Failed to complete order.");
                        console.error(err);
                      }
                    }}
                  >
                    Complete Order
                  </button>
                  <button
                    className="bg-purple-600 text-white px-4 py-2 rounded"
                    onClick={() => window.print()}
                  >
                    Print Order
                  </button>
                </div>
              </div>
            ) : (
              // Single-product order modal (existing behavior)
              <div>
                <p className="mb-2">
                  Order ID: <strong>{selectedOrderToComplete.orderId}</strong>
                </p>
                <input
                  type="number"
                  placeholder="Enter final weight"
                  value={finalWeightInput}
                  onChange={(e) => setFinalWeightInput(e.target.value)}
                  className="w-full border p-2 rounded mb-4"
                />
                <div className="flex justify-end space-x-2">
                  <button
                    className="bg-gray-400 text-white px-4 py-2 rounded"
                    onClick={() => setIsCompleteModalOpen(false)}
                  >
                    Close
                  </button>
                  <button
                    className="bg-green-600 text-white px-4 py-2 rounded"
                    onClick={async () => {
                      try {
                        await axios.put(
                          `/api/orders/complete-order/${selectedOrderToComplete._id}`,
                          {
                            finalWeights: JSON.stringify({
                              [selectedOrderToComplete.products[0]._id ||
                                selectedOrderToComplete.products[0].id]: finalWeightInput,
                            }),
                          }
                        );
                        alert("Final weight saved.");
                        fetchOrders();
                      } catch (err) {
                        alert("Failed to save final weight.");
                        console.error(err);
                      }
                    }}
                  >
                    Save
                  </button>
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                    onClick={async () => {
                      if (!finalWeightInput) {
                        alert("Please enter the final weight.");
                        return;
                      }
                      try {
                        await axios.put(
                          `/api/orders/complete-order/${selectedOrderToComplete._id}`,
                          {
                            finalWeights: JSON.stringify({
                              [selectedOrderToComplete.products[0]._id ||
                                selectedOrderToComplete.products[0].id]: finalWeightInput,
                            }),
                            complete: "true",
                          }
                        );
                        alert("Order completed.");
                        setIsCompleteModalOpen(false);
                        fetchOrders();
                      } catch (err) {
                        alert("Failed to complete order.");
                        console.error(err);
                      }
                    }}
                  >
                    Complete Order
                  </button>
                  <button
                    className="bg-purple-600 text-white px-4 py-2 rounded"
                    onClick={() => window.print()}
                  >
                    Print Order
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isUploadModalOpen}
        onRequestClose={() => setIsUploadModalOpen(false)}
        className="p-6 bg-white rounded-lg shadow max-w-xl mx-auto mt-20"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center"
      >
        <h2 className="text-xl font-bold mb-4">Upload Final Images</h2>
        {uploadOrder?.products?.map((product, idx) => (
          <div key={product._id || idx} className="mb-4 border p-3 rounded">
            <p className="font-semibold mb-2">
              Product ID: {product.productId}{" "}
              {product.productFinalImage ? (
                <span className="text-green-600">✅ Uploaded</span>
              ) : (
                <span className="text-red-600">❌ Not Uploaded</span>
              )}
            </p>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const formData = new FormData();
                  formData.append("productFinalImage", file);
                  formData.append("productId", product._id); // send product ID
                  axios
                    .post(`/api/orders/upload-photo/${uploadOrder._id}`, formData)
                    .then(() => {
                      alert("Photo uploaded successfully!");
                      fetchOrders();
                    })
                    .catch((error) => {
                      console.error(error);
                      alert("Error uploading photo.");
                    });
                }
              }}
              className="border p-2 rounded w-full"
            />
          </div>
        ))}

        <div className="flex justify-end mt-4">
          <button
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
            onClick={() => setIsUploadModalOpen(false)}
          >
            Close
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default OrderList;


